import { userDataCompletedForAnyProduct } from 'apps/widget/src/model/helpers';
import { MatchingTypeProps } from 'apps/widget/src/model/matchingType';
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { ClientReviewResponse, ProductWidget } from '@vestico/model';

import { GlobalsContext, Spinner, SpinnerPositioning, SpinnerSize, UserContext } from '@vestico/shared';

import { MainModalWindow } from '../../../components/MainModalWindow';
import { CarouselDesktop } from './CarouselDesktop';
import { CarouselMatchInfoModal } from './CarouselMatchInfoModal';
import { CarouselMobile } from './CarouselMobile';
import style from './CarouselModal.module.css';

export interface CarouselModalProps {
  initialResponseIndex?: number;
  anyResponseHasMatchInfo: boolean | undefined;
  anyResponseHasMatch: boolean | undefined;
  selectedResponse: ClientReviewResponse | undefined;
  onSelectedReponse: (response: ClientReviewResponse | undefined) => void;
  carouselIsOpen: boolean;
  setCarouselOpen: (open: boolean) => void;
  products: ProductWidget[] | null | undefined;
  hideContentInfoCTAContentCarousel?: boolean;
  onProductLinkClick?: (product: ProductWidget) => void;
}

export const CarouselModal = ({
  initialResponseIndex,
  anyResponseHasMatchInfo,
  anyResponseHasMatch,
  selectedResponse,
  onSelectedReponse,
  carouselIsOpen,
  setCarouselOpen,
  products,
  hideContentInfoCTAContentCarousel,
  onProductLinkClick,
  responses,
  sizeRecommendation,
  activeMatchingType,
  defaultMatchingType,
  onActiveMatchingTypeChange,
  hasContentMatchAvailable,
  hasSizeRecAvailable,
}: CarouselModalProps & MatchingTypeProps) => {
  const { useMobileLayout } = useContext(GlobalsContext);
  const { user } = useContext(UserContext);

  const [currentResponseIndex, setCurrentResponseIndex] = useState(initialResponseIndex);

  // We need to have an intermediate variable that determines what response to show while the current response index is being updated on appear
  const useCurrentResponseIdx = useMemo(() => carouselIsOpen, [carouselIsOpen]);

  const usedResponseIndex = useMemo(() => {
    return useCurrentResponseIdx ? currentResponseIndex : initialResponseIndex;
  }, [currentResponseIndex, initialResponseIndex, useCurrentResponseIdx]);

  useLayoutEffect(() => {
    if (!carouselIsOpen) {
      return;
    }
    setCurrentResponseIndex(initialResponseIndex);
  }, [initialResponseIndex, carouselIsOpen]);

  // Enter Body Info Modal

  const [showUserMatchInfo, setShowUserMatchInfo] = useState(false);
  const onBeginUserMatchInfo = useCallback(() => {
    setShowUserMatchInfo(true);
  }, []);

  const onUserMatchInfoClose = useCallback(() => {
    setShowUserMatchInfo(false);
  }, []);

  // Navigation
  const hasNext = useCallback(() => {
    return responses && currentResponseIndex !== undefined && currentResponseIndex < responses.length - 1;
  }, [responses, currentResponseIndex]);

  const hasPrevious = useCallback(() => {
    return currentResponseIndex !== undefined && currentResponseIndex > 0;
  }, [currentResponseIndex]);

  const goToNext = useCallback(() => {
    if (!hasNext() || currentResponseIndex === undefined) {
      return;
    }

    setCurrentResponseIndex(currentResponseIndex + 1);
  }, [hasNext, currentResponseIndex]);

  const goToPrevious = useCallback(() => {
    if (!hasPrevious() || currentResponseIndex === undefined) {
      return;
    }

    setCurrentResponseIndex(currentResponseIndex - 1);
  }, [hasPrevious, currentResponseIndex]);

  useEffect(() => {
    if (!responses) {
      return;
    }

    if (currentResponseIndex === undefined) {
      onSelectedReponse(undefined);
    } else {
      onSelectedReponse(responses[currentResponseIndex]);
    }
  }, [currentResponseIndex]);

  useLayoutEffect(() => {
    if (!responses || currentResponseIndex === undefined || !useCurrentResponseIdx) {
      return;
    }

    // re-select the same response if available
    const currentlyShownResponse = responses[currentResponseIndex];
    const currentResponseSelected =
      selectedResponse && currentlyShownResponse && selectedResponse.postDocumentID === currentlyShownResponse.postDocumentID;

    if (!currentResponseSelected && selectedResponse) {
      for (let idx = 0; idx < responses.length; idx++) {
        const response = responses[idx];
        if (response.postDocumentID === selectedResponse.postDocumentID) {
          setCurrentResponseIndex(idx);
          break;
        }
      }
    }
  }, [responses]);

  const onResponseIndexChange = useCallback((newIndex: number) => {
    setCurrentResponseIndex(newIndex);
  }, []);

  const onCarouselClose = useCallback(() => {
    setCarouselOpen(false);
    setCurrentResponseIndex(undefined);
    onSelectedReponse(undefined);
  }, [onSelectedReponse, setCarouselOpen]);

  const showBeginUserMatchInfoSectionIfNeeded = useMemo(
    () =>
      !hideContentInfoCTAContentCarousel &&
      anyResponseHasMatchInfo &&
      products !== null &&
      (!user || !userDataCompletedForAnyProduct(products, user)),
    [anyResponseHasMatchInfo, hideContentInfoCTAContentCarousel, products, user]
  );

  const content = useMemo(() => {
    if (!responses) {
      return (
        <div className={clsx(style.spinner)}>
          <Spinner size={SpinnerSize.medium} positioning={SpinnerPositioning.transformRelative} />
        </div>
      );
    }
    if (useMobileLayout) {
      return (
        <div className={clsx(style.root)}>
          <CarouselMobile
            responseIndex={usedResponseIndex}
            responses={responses}
            onBeginUserMatchInfo={onBeginUserMatchInfo}
            onResponseIndexChange={onResponseIndexChange}
            onClose={onCarouselClose}
            showBeginUserMatchInfoSection={showBeginUserMatchInfoSectionIfNeeded}
            showResetUserButton={!hideContentInfoCTAContentCarousel}
            anyResponseHasMatch={anyResponseHasMatch}
            products={products}
            onProductLinkClick={onProductLinkClick}
            activeMatchingType={activeMatchingType}
            defaultMatchingType={defaultMatchingType}
            onActiveMatchingTypeChange={onActiveMatchingTypeChange}
            hasContentMatchAvailable={hasContentMatchAvailable}
            hasSizeRecAvailable={hasSizeRecAvailable}
            sizeRecommendation={sizeRecommendation}
          />
        </div>
      );
    }
    return (
      <CarouselDesktop
        showBodyInfoForm={false}
        responseIndex={usedResponseIndex}
        responses={responses}
        onBeginUserMatchInfo={onBeginUserMatchInfo}
        onResponseIndexChange={onResponseIndexChange}
        showBeginUserMatchInfoSection={showBeginUserMatchInfoSectionIfNeeded}
        showResetUserButton={!hideContentInfoCTAContentCarousel}
        anyResponseHasMatch={anyResponseHasMatch}
        products={products}
        onProductLinkClick={onProductLinkClick}
        activeMatchingType={activeMatchingType}
        defaultMatchingType={defaultMatchingType}
        onActiveMatchingTypeChange={onActiveMatchingTypeChange}
        hasContentMatchAvailable={hasContentMatchAvailable}
        hasSizeRecAvailable={hasSizeRecAvailable}
        sizeRecommendation={sizeRecommendation}
      />
    );
  }, [
    responses,
    useMobileLayout,
    usedResponseIndex,
    onBeginUserMatchInfo,
    onResponseIndexChange,
    showBeginUserMatchInfoSectionIfNeeded,
    hideContentInfoCTAContentCarousel,
    anyResponseHasMatch,
    products,
    onProductLinkClick,
    activeMatchingType,
    defaultMatchingType,
    onActiveMatchingTypeChange,
    hasContentMatchAvailable,
    hasSizeRecAvailable,
    sizeRecommendation,
    onCarouselClose,
  ]);

  return (
    <div style={{ zIndex: 2147483647, position: 'fixed' }}>
      <MainModalWindow
        isOpen={carouselIsOpen}
        setOpen={setCarouselOpen}
        buttonsDisabled={showUserMatchInfo}
        goToNext={hasNext() && activeMatchingType === 'content' ? goToNext : undefined}
        goToPrevious={hasPrevious() && activeMatchingType === 'content' ? goToPrevious : undefined}
        hideReset={hideContentInfoCTAContentCarousel}
      >
        {content}
        <CarouselMatchInfoModal
          category={products?.[0]?.matchingCategory}
          showUserMatchInfo={showUserMatchInfo}
          onUserMatchInfoClose={onUserMatchInfoClose}
        />
      </MainModalWindow>
    </div>
  );
};

export default CarouselModal;
